import { LockKeyhole, RotateCcw, ScanFace } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { MediaInput } from './components/MediaInput';
import { ResultsPanel } from './components/ResultsPanel';
import { SettingsPanel } from './components/SettingsPanel';
import { initialConfig } from './domain/analysis';
import { translateError } from './i18n/errors';
import { useLocale, useTranslate } from './i18n/useI18n';
import { trackEvent } from './lib/analytics';
import { analyzeMedia } from './lib/faceAnalysis';
import { registerFaceAnalysisTool } from './lib/webmcp';
import { parentSiteUrl } from './site/siteLinks';
import { routeForKey } from './site/siteRoutes';
import { ThemeControl } from './theme/ThemeControl';

import type { AnalysisConfig, OutputSettings } from './domain/analysis';
import type { MediaRole, MediaSource } from './domain/media';
import type { LocalePreference, Translate } from './i18n/types';
import type { AnalysisProgress, AnalysisResult } from './lib/faceAnalysis';
import type { ThemePreference } from './theme/useTheme';
import type { ReactElement } from 'react';

type JobStatus = 'idle' | 'running' | 'success' | 'error';

const localePreferences: readonly LocalePreference[] = ['auto', 'en', 'tr'];

function progressMessage(progress: AnalysisProgress | undefined, translate: Translate): string {
    if (progress === undefined) return translate('status.preparing');
    if (progress.stage === 'models') {
        return translate('status.modelsPreparing', { count: progress.count });
    }
    return translate(
        progress.stage === 'reference' ? 'status.referenceFaces' : 'status.targetFaces'
    );
}

export function App(): ReactElement {
    const translate = useTranslate();
    const { locale, localePreference, setLocalePreference } = useLocale();
    const [config, setConfig] = useState<AnalysisConfig>(initialConfig);
    const [media, setMedia] = useState<Record<MediaRole, MediaSource | null>>({
        reference: null,
        haystack: null,
    });
    const [ready, setReady] = useState<Record<MediaRole, boolean>>({
        reference: false,
        haystack: false,
    });
    const [status, setStatus] = useState<JobStatus>('idle');
    const [progress, setProgress] = useState<AnalysisProgress>();
    const [error, setError] = useState<unknown>();
    const [result, setResult] = useState<AnalysisResult>();
    const [labels, setLabels] = useState<string[]>([]);
    const mediaElements = useRef<Record<MediaRole, HTMLImageElement | HTMLVideoElement | null>>({
        reference: null,
        haystack: null,
    });
    const runId = useRef(0);
    const configRef = useRef(config);

    useEffect(() => {
        document.title = translate('app.title');
        document
            .querySelector<HTMLMetaElement>('meta[name="description"]')
            ?.setAttribute('content', translate('app.description'));
    }, [translate]);
    useEffect(() => {
        configRef.current = config;
    }, [config]);
    useEffect(() => {
        const source = media.reference;
        return () => {
            if (source?.origin === 'file') URL.revokeObjectURL(source.previewUrl);
        };
    }, [media.reference]);
    useEffect(() => {
        const source = media.haystack;
        return () => {
            if (source?.origin === 'file') URL.revokeObjectURL(source.previewUrl);
        };
    }, [media.haystack]);

    const invalidateResult = useCallback(() => {
        runId.current += 1;
        setResult(undefined);
        setError(undefined);
        setStatus('idle');
        setProgress(undefined);
    }, []);
    const handleConfigChange = useCallback(
        (next: AnalysisConfig) => {
            setConfig(next);
            invalidateResult();
        },
        [invalidateResult]
    );
    // Drawing settings only affect how an existing result is rendered, so they keep it.
    const handleOutputChange = useCallback((output: OutputSettings) => {
        setConfig((current) => ({ ...current, output }));
    }, []);
    const handleSourceChange = useCallback(
        (role: MediaRole, source: MediaSource | null) => {
            setMedia((current) => ({ ...current, [role]: source }));
            setReady((current) => ({ ...current, [role]: false }));
            invalidateResult();
        },
        [invalidateResult]
    );
    const handleReadyChange = useCallback((role: MediaRole, isReady: boolean) => {
        setReady((current) =>
            current[role] === isReady ? current : { ...current, [role]: isReady }
        );
    }, []);
    const setMediaElement = useCallback(
        (role: MediaRole, element: HTMLImageElement | HTMLVideoElement | null) => {
            mediaElements.current[role] = element;
        },
        []
    );

    useEffect(
        () =>
            registerFaceAnalysisTool({
                getConfig: () => configRef.current,
                setConfig,
                setMedia: (nextMedia) => {
                    setMedia(nextMedia);
                    setReady({ reference: false, haystack: false });
                },
                invalidate: invalidateResult,
                translate,
            }),
        [invalidateResult, translate]
    );

    const canRun =
        ready.haystack && (!config.features.matching || ready.reference) && status !== 'running';
    const translatedError =
        error === undefined ? undefined : translateError(error, translate, 'error.unexpected');
    const runHint = useMemo(() => {
        if (status === 'running') return progressMessage(progress, translate);
        if (status === 'error') return translatedError ?? translate('status.analysisFailed');
        if (status === 'success' && result !== undefined) {
            return translate('status.targetsAnalyzed', { count: result.haystack.length });
        }
        if (!ready.haystack) return translate('status.addTarget');
        if (config.features.matching && !ready.reference) return translate('status.addReference');
        return translate('status.ready');
    }, [
        config.features.matching,
        progress,
        ready.haystack,
        ready.reference,
        result,
        status,
        translate,
        translatedError,
    ]);

    const handleRun = async (): Promise<void> => {
        const haystackMedia = mediaElements.current.haystack;
        const referenceMedia = mediaElements.current.reference;
        if (haystackMedia === null || (config.features.matching && referenceMedia === null)) return;
        if (haystackMedia instanceof HTMLVideoElement) haystackMedia.pause();
        if (referenceMedia instanceof HTMLVideoElement) referenceMedia.pause();
        const currentRunId = runId.current + 1;
        runId.current = currentRunId;
        setStatus('running');
        setError(undefined);
        setResult(undefined);
        setProgress(undefined);
        trackEvent('analysis_started', {
            detector: config.detector,
            matching: config.features.matching,
            expressions: config.features.expressions,
            age_and_gender: config.features.ageAndGender,
        });
        try {
            const nextResult = await analyzeMedia({
                referenceMedia: referenceMedia ?? undefined,
                haystackMedia,
                config,
                onProgress: setProgress,
            });
            if (runId.current !== currentRunId) return;
            setResult(nextResult);
            setLabels((current) => nextResult.reference.map((_, index) => current[index] ?? ''));
            setStatus('success');
            setProgress(undefined);
            trackEvent('analysis_completed', {
                detector: config.detector,
                target_face_count: nextResult.haystack.length,
                reference_face_count: nextResult.reference.length,
                duration_ms: Math.round(nextResult.durationMs),
            });
        } catch (caught) {
            if (runId.current !== currentRunId) return;
            setError(caught);
            setStatus('error');
            trackEvent('analysis_failed', { detector: config.detector, reason: 'runtime_error' });
        }
    };

    const reset = (): void => {
        runId.current += 1;
        setConfig(initialConfig);
        setMedia({ reference: null, haystack: null });
        setReady({ reference: false, haystack: false });
        setResult(undefined);
        setLabels([]);
        setError(undefined);
        setStatus('idle');
        setProgress(undefined);
    };

    const referenceFaces = result?.reference ?? [];
    const haystackFaces = result?.haystack ?? [];
    const homeHref = routeForKey('home', locale).path;
    const themeLabels: Readonly<Record<ThemePreference, string>> = {
        system: translate('theme.system'),
        light: translate('theme.light'),
        dark: translate('theme.dark'),
    };

    return (
        <div className='app-shell'>
            <header className='topbar'>
                <a
                    className='brand'
                    href={homeHref}
                    aria-label={translate('header.home')}
                >
                    <span className='brand-mark'>
                        <ScanFace aria-hidden='true' />
                    </span>
                    <span>FACE LAB</span>
                </a>
                <div className='topbar-controls'>
                    <div className='privacy-note'>
                        <LockKeyhole aria-hidden='true' />
                        <span>{translate('header.privacy')}</span>
                    </div>
                    <ThemeControl
                        label={translate('theme.label')}
                        labels={themeLabels}
                        nextLabel={(theme) => translate('theme.next', { theme })}
                    />
                    <select
                        className='language-select'
                        value={localePreference}
                        aria-label={translate('language.label')}
                        onChange={(event) => {
                            const nextPreference = event.target.value;
                            if (localePreferences.includes(nextPreference as LocalePreference)) {
                                setLocalePreference(nextPreference as LocalePreference);
                            }
                        }}
                    >
                        {localePreferences.map((preference) => (
                            <option
                                key={preference}
                                value={preference}
                            >
                                {translate(`language.${preference}`)}
                            </option>
                        ))}
                    </select>
                    <button
                        className='reset-button'
                        type='button'
                        onClick={reset}
                    >
                        <RotateCcw aria-hidden='true' /> <span>{translate('header.reset')}</span>
                    </button>
                </div>
            </header>

            <main className='workspace'>
                <div className='workspace-grid'>
                    <div className='media-grid'>
                        <MediaInput
                            key={media.reference?.id ?? 'reference-empty'}
                            role='reference'
                            source={media.reference}
                            disabled={!config.features.matching || status === 'running'}
                            faces={referenceFaces}
                            labels={labels}
                            output={config.output}
                            matching={config.features.matching}
                            onSourceChange={(source) => {
                                handleSourceChange('reference', source);
                            }}
                            onReadyChange={(isReady) => {
                                handleReadyChange('reference', isReady);
                            }}
                            onMediaElement={(element) => {
                                setMediaElement('reference', element);
                            }}
                            onFrameChange={invalidateResult}
                            onOutputChange={handleOutputChange}
                        />
                        <div
                            className='flow-arrow'
                            aria-hidden='true'
                        >
                            <span>×</span>
                        </div>
                        <MediaInput
                            key={media.haystack?.id ?? 'haystack-empty'}
                            role='haystack'
                            source={media.haystack}
                            disabled={status === 'running'}
                            faces={haystackFaces}
                            labels={labels}
                            output={config.output}
                            matching={config.features.matching}
                            onSourceChange={(source) => {
                                handleSourceChange('haystack', source);
                            }}
                            onReadyChange={(isReady) => {
                                handleReadyChange('haystack', isReady);
                            }}
                            onMediaElement={(element) => {
                                setMediaElement('haystack', element);
                            }}
                            onFrameChange={invalidateResult}
                            onOutputChange={handleOutputChange}
                        />
                    </div>
                    <SettingsPanel
                        config={config}
                        isRunning={status === 'running'}
                        canRun={canRun}
                        runHint={runHint}
                        onChange={handleConfigChange}
                        onOutputChange={handleOutputChange}
                        onRun={() => {
                            handleRun().catch((caught: unknown) => {
                                setError(caught);
                                setStatus('error');
                            });
                        }}
                    />
                </div>
                {status === 'error' && (
                    <div
                        className='error-banner'
                        role='alert'
                    >
                        <strong>{translate('status.analysisFailed')}</strong>
                        <span>{translatedError}</span>
                    </div>
                )}
                {result !== undefined && (
                    <ResultsPanel
                        result={result}
                        config={config}
                        labels={labels}
                        onLabelChange={(index, label) => {
                            setLabels((current) =>
                                current.map((value, currentIndex) =>
                                    currentIndex === index ? label : value
                                )
                            );
                        }}
                    />
                )}
            </main>
            <footer className='footer'>
                <span>{translate('footer.engine')}</span>
                <a href={homeHref}>{translate('footer.site')}</a>
                <a href={routeForKey('privacy', locale).path}>{translate('footer.privacy')}</a>
                <a href={routeForKey('terms', locale).path}>{translate('footer.terms')}</a>
                <a
                    href={parentSiteUrl}
                    target='_blank'
                    rel='noopener noreferrer'
                >
                    {translate('footer.parentSite')}
                </a>
            </footer>
        </div>
    );
}
